/* eslint-disable no-console */
import React, { useState, useEffect, useReducer, useRef, useCallback } from "react";
import styled from "styled-components";
import {
    Box,
    Button,
    TextField,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Snackbar,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Card,
    CardContent,
    CardActions,
    Typography,
    Chip,
    Alert
} from "@mui/material";
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Image as ImageIcon
} from "@mui/icons-material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useFeedAds, useFeedAdsUtils } from "../actions/feedAds";
import { THEME_MAP } from "../theme";
import { ArrowForwardIos } from "@mui/icons-material";

const Container = styled.div`
    height: 100%;
    width: 100%;
    padding: 24px;
    overflow: auto;
    position: relative;
`;

const SectionCard = styled(Card)`
    margin-bottom: 24px;
`;

const AdCardStyled = styled(Card)`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    /* Remove fixed height if any, let content define it */
    /* Add transition for smoother hover effects if desired */
    transition: box-shadow 0.3s ease-in-out;
    &:hover {
        box-shadow: 0px 4px 12px rgba(0,0,0,0.1); /* Subtle hover effect */
    }
`;

const AdListCardContent = styled(CardContent)`
    padding: 12px !important; /* Reduce padding */
    flex-grow: 1; /* Allow content to grow */
    /* Reduce bottom padding more if actions are present */
    padding-bottom: 4px !important;
`;

const AdImagePreview = styled.img`
    max-width: 100%;
    height: 100px; /* Reduce image height */
    object-fit: cover;
    background-color: ${THEME_MAP["color-basic-300"]};
    display: block; /* Remove extra space below image */
    border-radius: 4px 4px 0 0; /* Optional: round top corners */
`;

const AdListContainer = styled.div`
    display: grid;
    /* Adjust minmax to allow slightly smaller cards, potentially fitting more */
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 16px;
    margin-top: 16px;
`;

const FormGrid = styled(Grid)`
    padding-bottom: 16px;
`;

const PreviewContainer = styled.div`
    margin-top: 16px;
    padding: 0px;
    border: 1px dashed ${THEME_MAP["color-basic-400"]};
    border-radius: 4px;
    background-color: ${THEME_MAP["color-basic-200"]};
`;

const AdCardPreviewInFeed = styled(Card)`
    /* Styles similar to mobile AdCard */
    margin: 0; /* Reset margin for preview */
    border: 1px solid ${THEME_MAP["color-primary-300"]}; /* Highlight the ad */
    border-radius: 8px; /* Match mobile borderRadius */
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1); /* Match mobile shadow */
    overflow: hidden; /* Ensure content doesn't overflow rounded corners */
`;

const AdImagePreviewInFeed = styled.img`
    width: 100%;
    height: 160px; /* Match mobile imageContainer height */
    object-fit: cover;
    background-color: ${THEME_MAP["color-basic-300"]};
    display: block;
    border-radius: 8px 8px 0 0; /* Round only top corners to match container */
    margin: -1px -1px 0 -1px; /* Extend to container edges, accounting for border */
`;

const AdPreviewContent = styled(CardContent)`
    position: relative;
    padding: 12px !important; /* Match mobile infoContainer padding */

    .ad-label {
        position: absolute;
        top: 8px;
        left: 8px;
        background-color: ${THEME_MAP["control-background-color"] || "rgba(255, 255, 255, 0.9)"};
        color: ${THEME_MAP["text-basic-color"] || "#000"};
        padding: 3px 6px; /* Match mobile adLabel padding */
        font-size: 10px;
        font-weight: bold; /* Match mobile fontWeight */
        border-radius: 4px;
        z-index: 1;
    }
`;

const AdCompanyName = styled(Typography)`
    padding-left: 12px;
    padding-right: 12px;
    font-weight: bold !important;
    font-size: 18px !important; /* Match mobile companyNameText */
    color: ${THEME_MAP["text-basic-color"]} !important;
    margin-bottom: 4px !important;
`;

const AdTitle = styled(Typography)`
    padding-left: 12px;
    padding-right: 12px;
    font-weight: bold !important;
    font-size: 22px !important; /* Match mobile adTitleText */
    color: ${THEME_MAP["color-primary-500"]} !important;
    margin-bottom: 6px !important;
`;

const AdDescription = styled(Typography)`\
    padding-left: 12px;
    padding-right: 12px;
    font-weight: normal !important;
    font-size: 13px !important; /* Match mobile adDescriptionText */
    color: ${THEME_MAP["text-hint-color"]} !important;
    line-height: 18px !important;
    margin-bottom: 10px !important;
    /* Remove noWrap to allow text wrapping */
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: unset !important;
`;

const AdCTAContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    margin-top: 4px;
    padding: 0 12px 12px 12px; /* Match mobile infoContainer padding */
`;

const AdCTAText = styled(Typography)`
    font-weight: 600 !important;
    font-size: 14px !important;
    color: ${THEME_MAP["color-primary-500"]} !important;
    margin-right: 4px !important;
`;

const FileInput = styled.input`
  display: none;
`;

const initialAdState = {
    id: null,
    name: "",
    status: "inactive",
    startDate: null,
    endDate: null,
    companyName: "",
    creative: {
        type: "image",
        title: "",
        description: "",
        imageUrl: "",
        aspectRatio: 1.5,
        fileName: ""
    },
    action: {
        type: "url",
        url: ""
    },
    frequencyWeight: 50,
    metrics: { impressions: 0, clicks: 0 },
};

function adReducer(state, action) {
    switch (action.type) {
        case "LOAD_AD": {
            const creative = action.payload.creative || {};
            const actionData = action.payload.action || {};
            const metrics = action.payload.metrics || { impressions: 0, clicks: 0 };
            return {
                ...initialAdState,
                ...action.payload,
                creative: { ...initialAdState.creative, ...creative },
                action: { ...initialAdState.action, ...actionData },
                metrics: { ...initialAdState.metrics, ...metrics },
            };
        }
        case "UPDATE_FIELD":
            return { ...state, [action.payload.name]: action.payload.value };
        case "UPDATE_NESTED_FIELD": {
            const keys = action.payload.name.split(".");
            let newState = { ...state };
            let currentLevel = newState;
            for (let i = 0; i < keys.length - 1; i++) {
                if (!currentLevel[keys[i]]) {
                    currentLevel[keys[i]] = {};
                }
                currentLevel = currentLevel[keys[i]];
            }
            currentLevel[keys[keys.length - 1]] = action.payload.value;
            return newState;
        }
        case "SET_IMAGE":
            return {
                ...state,
                creative: {
                    ...state.creative,
                    imageUrl: action.payload.imageUrl,
                    fileName: action.payload.fileName,
                }
            };
        case "RESET":
            return initialAdState;
        default:
            return state;
    }
}


export function FeedAds() {
    const { ads, settings, isLoading, error } = useFeedAds();
    const { saveSetting, saveAd, deleteAd, uploadAdImage } = useFeedAdsUtils();

    const [showAdModal, setShowAdModal] = useState(false);
    const [adState, dispatchAd] = useReducer(adReducer, initialAdState);
    const [isEditing, setIsEditing] = useState(false);
    const [currentAdInterval, setCurrentAdInterval] = useState(settings?.adInterval || 10);

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const fileInputRef = useRef(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
    const [isUploading, setIsUploading] = useState(false);


    useEffect(() => {
        if (settings?.adInterval !== undefined) {
            setCurrentAdInterval(settings.adInterval);
        }
    }, [settings]);

    useEffect(() => {
        if (error) {
            showSnackbar(error, "error");
        }
    }, [error]);

    const handleOpenModal = useCallback((ad = null) => {
        if (ad) {
            setIsEditing(true);
            const startDate = ad.startDate?.toDate ? dayjs(ad.startDate.toDate()) : null;
            const endDate = ad.endDate?.toDate ? dayjs(ad.endDate.toDate()) : null;

            dispatchAd({
                type: "LOAD_AD", payload: {
                    ...ad,
                    startDate,
                    endDate,
                }
            });
            setImagePreviewUrl(ad.creative?.imageUrl || null);
        } else {
            setIsEditing(false);
            dispatchAd({ type: "RESET" });
            setImagePreviewUrl(null);
        }
        setShowAdModal(true);
    }, []);

    const handleCloseModal = () => {
        setShowAdModal(false);
        setImagePreviewUrl(null);
        // dispatchAd({ type: 'RESET' });
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        if (name.includes(".")) {
            dispatchAd({ type: "UPDATE_NESTED_FIELD", payload: { name, value } });
        } else {
            dispatchAd({ type: "UPDATE_FIELD", payload: { name, value } });
        }
    };

    const handleDateChange = (name) => (date) => {
        dispatchAd({ type: "UPDATE_FIELD", payload: { name, value: date } });
    };

    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (file) {
            if (!file.type.startsWith("image/")) {
                showSnackbar("Please select an image file.", "warning");
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);

            setIsUploading(true);
            try {
                const { fileName, downloadUrl } = await uploadAdImage(file);

                dispatchAd({ type: "SET_IMAGE", payload: { imageUrl: downloadUrl, fileName: fileName } });

                showSnackbar("Image uploaded successfully.", "success");
            } catch (uploadError) {
                console.error("Image upload failed:", uploadError);
                showSnackbar("Image upload failed. Please try again.", "error");

                setImagePreviewUrl(adState.creative?.imageUrl || null); // Revert to original if editing
            } finally {
                setIsUploading(false);
            }
        }
    };

    const triggerFileUpload = () => {
        // eslint-disable-next-line no-unused-expressions
        fileInputRef.current?.click();
    };

    const handleSaveSettings = async () => {
        const success = await saveSetting("adInterval", currentAdInterval);
        if (success) {
            showSnackbar("Settings saved successfully.", "success");
        } else {
            // Error is handled by the context and shown in the useEffect
        }
    };

    const handleSaveAd = async () => {
        if (!adState.name || !adState.companyName || !adState.creative.title || !adState.action.url || !adState.creative.imageUrl) {
            showSnackbar("Please fill in all required fields and upload an image.", "warning");
            return;
        }
        if (adState.creative.imageUrl && adState.creative.imageUrl.startsWith("data:")) {
            showSnackbar("Please wait for image upload to complete.", "warning");
            return;
        }

        const savedAd = await saveAd(adState);

        if (savedAd) {
            showSnackbar(`Ad ${isEditing ? "updated" : "created"} successfully.`, "success");
            handleCloseModal();
        } else {
            // Error handled by context, but could show specific message
        }
    };

    const handleDeleteAd = async (adToDelete) => {
        if (!adToDelete || !adToDelete.id) return;
        if (!window.confirm(`Are you sure you want to delete the ad "${adToDelete.name}"? This action cannot be undone.`)) {
            return;
        }
        const success = await deleteAd(adToDelete.id, adToDelete.creative?.fileName);

        if (success) {
            showSnackbar("Ad deleted successfully.", "success");
        } else {
            // Error handled by context
        }
    };

    const showSnackbar = (message, severity = "info") => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Container>
                {(isLoading || isUploading) && <LinearProgress style={{ position: "absolute", top: 0, left: 0, right: 0 }} />}

                <SectionCard>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>General Feed Ad Settings</Typography>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={4}>
                                <Tooltip title="Determines how frequently an ad appears in the user's feed (e.g., 10 means roughly 1 ad per 10 bars).">
                                    <TextField
                                        label="Ad Interval"
                                        type="number"
                                        name="adInterval"
                                        value={currentAdInterval}
                                        onChange={(e) => setCurrentAdInterval(parseInt(e.target.value, 10) || 0)}
                                        fullWidth
                                        InputProps={{ inputProps: { min: 1 } }}
                                        disabled={settings?.adInterval === undefined || isLoading}
                                    />
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </CardContent>
                    <CardActions>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSaveSettings}
                            disabled={isLoading || currentAdInterval === settings?.adInterval}
                        >
                            Save Settings
                        </Button>
                    </CardActions>
                </SectionCard>

                <SectionCard>
                    <CardContent>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                            <Typography variant="h6">Manage Feed Ads</Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenModal()}
                                disabled={isLoading}
                            >
                                Create Ad
                            </Button>
                        </div>

                        {ads.length === 0 && !isLoading ? (
                            <Typography>No feed ads created yet.</Typography>
                        ) : (
                            <AdListContainer>
                                {ads.map((ad) => (
                                    <AdCardStyled key={ad.id}>
                                        <AdListCardContent>
                                            {ad.creative?.imageUrl && (
                                                <AdImagePreview src={ad.creative.imageUrl} alt={ad.name} />
                                            )}
                                            <Typography variant="subtitle2"
                                                fontWeight="bold"
                                                gutterBottom
                                                noWrap
                                                sx={{ mt: ad.creative?.imageUrl ? 1.5 : 0, mb: 0.5 }}>
                                                {ad.name} ({ad.companyName})
                                            </Typography>
                                            <Typography variant="caption"
                                                color="textSecondary"
                                                noWrap
                                                display="block"
                                                sx={{ mb: 1 }}>
                                                {ad.creative?.title}
                                            </Typography>
                                            {/* Combine Status Chip and Weight */}
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                                                <Chip
                                                    label={ad.status}
                                                    size="small"
                                                    color={ad.status === "active" ? "success" : "default"}
                                                />
                                                <Typography variant="caption" color="textSecondary" noWrap>
                                                    Weight: {ad.frequencyWeight || "N/A"}
                                                </Typography>
                                            </Box>
                                        </AdListCardContent>
                                        <CardActions sx={{ justifyContent: "flex-end", pt: 0, pb: 1, px: 1 }}>
                                            <Tooltip title="Edit Ad">
                                                <IconButton size="small" onClick={() => handleOpenModal(ad)} disabled={isLoading}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete Ad">
                                                <IconButton size="small" onClick={() => handleDeleteAd(ad)} disabled={isLoading}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </CardActions>
                                    </AdCardStyled>
                                ))}
                            </AdListContainer>
                        )}
                    </CardContent>
                </SectionCard>

                <Dialog open={showAdModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
                    <DialogTitle>{isEditing ? "Edit Feed Ad" : "Create New Feed Ad"}</DialogTitle>
                    <DialogContent>
                        {(isLoading || isUploading) && <LinearProgress sx={{ mb: 2 }} />}
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom>Ad Details</Typography>
                                <FormGrid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField label="Ad Name (Internal)" name="name" value={adState.name} onChange={handleInputChange} fullWidth required disabled={isLoading || isUploading} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Company Name"
                                            name="companyName"
                                            value={adState.companyName}
                                            onChange={handleInputChange}
                                            fullWidth
                                            required
                                            disabled={isLoading || isUploading}
                                            inputProps={{ maxLength: 30 }}
                                            helperText={`${adState.companyName.length}/30 characters`}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth required disabled={isLoading || isUploading}>
                                            <InputLabel>Status</InputLabel>
                                            <Select name="status" value={adState.status} label="Status" onChange={handleInputChange}>
                                                <MenuItem value="active">Active</MenuItem>
                                                <MenuItem value="inactive">Inactive</MenuItem>
                                                {/* <MenuItem value="scheduled">Scheduled</MenuItem> */}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Tooltip title="Determines the likelihood of this ad being shown compared to others. Higher weight means shown more often. 1-100 scale.">
                                            <TextField
                                                label="Frequency Weight"
                                                name="frequencyWeight"
                                                type="number"
                                                value={adState.frequencyWeight}
                                                onChange={handleInputChange}
                                                fullWidth
                                                required
                                                InputProps={{ inputProps: { min: 1, max: 100 } }}
                                                disabled={isLoading || isUploading}
                                            />
                                        </Tooltip>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <DatePicker
                                            label="Start Date (Optional)"
                                            value={adState.startDate}
                                            onChange={handleDateChange("startDate")}
                                            renderInput={(params) => <TextField {...params} fullWidth />}
                                            disabled={isLoading || isUploading}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <DatePicker
                                            label="End Date (Optional)"
                                            value={adState.endDate}
                                            onChange={handleDateChange("endDate")}
                                            minDate={adState.startDate || undefined}
                                            renderInput={(params) => <TextField {...params} fullWidth />}
                                            disabled={isLoading || isUploading}
                                        />
                                        <Typography variant="caption">Leave blank for no end date.</Typography>
                                    </Grid>
                                </FormGrid>

                                <Typography variant="subtitle2" gutterBottom style={{ marginTop: "16px" }}>Creative</Typography>
                                <FormGrid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Ad Title"
                                            name="creative.title"
                                            value={adState.creative.title}
                                            onChange={handleInputChange}
                                            fullWidth
                                            required
                                            disabled={isLoading || isUploading}
                                            inputProps={{ maxLength: 32 }}
                                            helperText={`${adState.creative.title.length}/32 characters`}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Ad Description"
                                            name="creative.description"
                                            value={adState.creative.description}
                                            onChange={handleInputChange}
                                            fullWidth
                                            multiline
                                            rows={3}
                                            disabled={isLoading || isUploading}
                                            inputProps={{ maxLength: 100 }}
                                            helperText={`${adState.creative.description.length}/100 characters`}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FileInput
                                            type="file"
                                            accept="image/*"
                                            ref={fileInputRef}
                                            onChange={handleFileSelect}
                                            disabled={isLoading || isUploading}
                                        />
                                        <Button
                                            variant="outlined"
                                            startIcon={<ImageIcon />}
                                            onClick={triggerFileUpload}
                                            disabled={isLoading || isUploading}
                                        >
                                            {isUploading ? "Uploading..." : (imagePreviewUrl ? "Change Image" : "Upload Image *")}
                                        </Button>
                                        {adState.creative.fileName && !isUploading && (
                                            <Typography variant="caption" style={{ marginLeft: "10px" }}>{adState.creative.fileName}</Typography>
                                        )}
                                    </Grid>
                                </FormGrid>

                                <Typography variant="subtitle2" gutterBottom style={{ marginTop: "16px" }}>Action</Typography>
                                <FormGrid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <FormControl fullWidth required disabled={isLoading || isUploading}>
                                            <InputLabel>Action Type</InputLabel>
                                            <Select
                                                name="action.type"
                                                value={adState.action.type}
                                                label="Action Type"
                                                onChange={handleInputChange}
                                            >
                                                <MenuItem value="url">Open URL</MenuItem>
                                                {/* Add other types later */}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={8}>
                                        <TextField
                                            label="Action URL"
                                            name="action.url"
                                            value={adState.action.url}
                                            onChange={handleInputChange}
                                            fullWidth
                                            required
                                            placeholder="https://example.com"
                                            disabled={isLoading || isUploading}
                                        />
                                    </Grid>
                                </FormGrid>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom>Preview (Mobile Feed)</Typography>
                                <PreviewContainer>
                                    <Typography variant="caption" display="block" gutterBottom>This is how the ad will appear in the feed:</Typography>

                                    <AdCardPreviewInFeed variant="outlined">
                                        <AdPreviewContent>
                                            <span className="ad-label">Ad</span>
                                            {(imagePreviewUrl || adState.creative.imageUrl) ? (
                                                <AdImagePreviewInFeed
                                                    src={imagePreviewUrl || adState.creative.imageUrl}
                                                    alt="Ad Preview"
                                                />
                                            ) : (
                                                <div style={{
                                                    height: 160,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    backgroundColor: THEME_MAP["color-basic-300"],
                                                    color: THEME_MAP["text-hint-color"]
                                                }}>
                                                    <ImageIcon />
                                                    <span style={{ marginLeft: "8px" }}>Advertisement</span>
                                                </div>
                                            )}

                                            <AdCompanyName variant="body1" noWrap>
                                                {adState.companyName || "Sponsored Content"}
                                            </AdCompanyName>

                                            <AdTitle variant="subtitle2" noWrap>
                                                {adState.creative.title || "Ad Title"}
                                            </AdTitle>

                                            <AdDescription variant="caption" sx={{
                                                display: "-webkit-box",
                                                WebkitLineClamp: 3, // Show up to 3 lines like mobile
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden"
                                            }}>
                                                {adState.creative.description || "Ad description..."}
                                            </AdDescription>
                                        </AdPreviewContent>

                                        <AdCTAContainer>
                                            <AdCTAText variant="body2">
                                                {adState.action?.ctaText || "Learn More"}
                                            </AdCTAText>
                                            <ArrowForwardIos
                                                style={{
                                                    width: 18,
                                                    height: 18,
                                                    color: THEME_MAP["color-primary-500"]
                                                }}
                                            />
                                        </AdCTAContainer>
                                    </AdCardPreviewInFeed>

                                </PreviewContainer>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseModal} disabled={isLoading || isUploading}>Cancel</Button>
                        <Button onClick={handleSaveAd} variant="contained" color="primary" disabled={isLoading || isUploading}>
                            {isLoading ? "Saving..." : (isEditing ? "Save Changes" : "Create Ad")}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                    <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Container>
        </LocalizationProvider>
    );
}

export default FeedAds;
